import type { AdminRouterContext } from '@/lib/router-context'
import { InfoNotice, OrganizationContextNotice } from '@/components/patterns/info-notice'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Typography } from '@/components/ui/typography'
import { useIsReadOnly } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import {
  apiTokenQueries,
  useApiToken,
  useCreateApiTokenMutation,
  useRegenerateApiTokenMutation,
  useRevokeApiTokenMutation,
  useValidateApiTokenMutation,
} from '@/lib/services/api-tokens/api-tokens.query'
import { apiTokenValidationSchema } from '@/lib/services/api-tokens/api-tokens.schema'
import { getErrorMessage } from '@/lib/utils'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import type { ApiTokenState } from '@/lib/services/api-tokens/api-tokens.types'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { Copy, KeyRound, RefreshCcw, Shield, Trash2 } from 'lucide-react'
import { useState, type SubmitEvent } from 'react'
import { toast } from 'sonner'

export const loadApiTokensRoute = async ({
  context,
  params,
}: {
  context: AdminRouterContext
  params: { orgId: number }
}) => {
  if (!context.ability.can('edit', 'api-tokens') || !context.ability.can('access', 'global')) {
    throw notFound()
  }

  await context.queryClient.ensureQueryData(apiTokenQueries.show({ organizationId: params.orgId }))

  return { isReadOnly: false }
}

export const Route = createFileRoute('/_authenticated/$orgId/api-tokens/')({
  component: ApiTokensPage,
  loader: loadApiTokensRoute,
})

function formatTimestamp(value: ApiTokenState['created_at'] | undefined) {
  return value ? dayjs(value).format('M/D/YYYY h:mm A') : 'Never'
}

async function copyToken(token: string) {
  try {
    await navigator.clipboard.writeText(token)
    toast.success('API token copied to clipboard')
  } catch (error) {
    toast.error('Failed to copy API token', {
      description: getErrorMessage(error),
    })
  }
}

export function ApiTokensPage() {
  const orgId = useRouteOrganizationIdParam()

  return <ApiTokensPageContent key={orgId} />
}

function ApiTokensPageContent() {
  const { data: tokenState } = useApiToken()
  const { isReadOnly } = useIsReadOnly()
  const createMutation = useCreateApiTokenMutation()
  const regenerateMutation = useRegenerateApiTokenMutation()
  const revokeMutation = useRevokeApiTokenMutation()
  const validateMutation = useValidateApiTokenMutation()
  const [plainTextToken, setPlainTextToken] = useState<string | null>(null)
  const [validationToken, setValidationToken] = useState('')
  const [validationResult, setValidationResult] = useState<'valid' | 'invalid' | null>(null)

  const canManage = tokenState?.can_manage && !isReadOnly
  const isBusy =
    createMutation.isPending ||
    regenerateMutation.isPending ||
    revokeMutation.isPending ||
    validateMutation.isPending
  const isStorefrontSystemToken = tokenState?.kind === 'storefront_system'
  const isGlobalReadOnlyState =
    isStorefrontSystemToken || tokenState?.kind === 'global_organization'

  const handleCreate = async () => {
    try {
      const response = await createMutation.mutateAsync()
      setPlainTextToken(response?.plain_text_token ?? null)
      setValidationResult(null)
      toast.success('API token created successfully')
    } catch (error) {
      toast.error('Failed to create API token', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleRegenerate = async () => {
    try {
      const response = await regenerateMutation.mutateAsync()
      setPlainTextToken(response?.plain_text_token ?? null)
      setValidationResult(null)
      toast.success('API token regenerated successfully')
    } catch (error) {
      toast.error('Failed to regenerate API token', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleRevoke = async () => {
    try {
      await revokeMutation.mutateAsync()
      setPlainTextToken(null)
      setValidationToken('')
      setValidationResult(null)
      toast.success('API token revoked successfully')
    } catch (error) {
      toast.error('Failed to revoke API token', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleValidate = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()

    const parsed = apiTokenValidationSchema.safeParse({ token: validationToken })

    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Please enter an API token')
      return
    }

    try {
      const response = await validateMutation.mutateAsync(parsed.data)
      setValidationResult(response?.is_valid ? 'valid' : 'invalid')
      setValidationToken('')
    } catch (error) {
      toast.error('Failed to validate API token', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className="max-w-4xl space-y-6">
      <AdminPageHeader
        icon={KeyRound}
        title="API Tokens"
        description="Generate and manage per-organization bearer tokens for internal API access."
      />

      {isGlobalReadOnlyState ? (
        <OrganizationContextNotice>
          {isStorefrontSystemToken
            ? 'This is the OneStem global organization. The storefront API token is read-only.'
            : 'This is a global organization. API token management is only available for florist organizations.'}
        </OrganizationContextNotice>
      ) : null}

      {plainTextToken ? (
        <InfoNotice
          title="Copy this token now"
          tone="warning"
          icon={<Shield />}
          body={
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input readOnly value={plainTextToken} aria-label="Plaintext API token" />
              <Button
                type="button"
                variant="secondary"
                onClick={() => copyToken(plainTextToken)}
                className="shrink-0"
              >
                <Copy />
                Copy
              </Button>
            </div>
          }
        >
          <Typography variant="supporting" className="block">
            This plaintext token is only shown immediately after creation or regeneration.
          </Typography>
        </InfoNotice>
      ) : null}

      {validationResult === 'valid' ? (
        <InfoNotice title="Token is valid" tone="success" icon={<Shield />}>
          The submitted token matches this organization&apos;s active API token.
        </InfoNotice>
      ) : null}

      {validationResult === 'invalid' ? (
        <InfoNotice title="Token is invalid" tone="danger" icon={<Shield />}>
          The submitted token does not match this organization&apos;s active API token.
        </InfoNotice>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            Token Status
            <Badge
              variant={
                tokenState?.is_protected ? 'warning' : tokenState?.exists ? 'success' : 'muted'
              }
            >
              {tokenState?.is_protected
                ? 'Protected'
                : tokenState?.exists
                  ? 'Active'
                  : 'Not created'}
            </Badge>
          </CardTitle>
          <CardDescription>
            {tokenState?.kind === 'storefront_system'
              ? 'This system token is used by the storefront and cannot be modified from admin.'
              : tokenState?.kind === 'global_organization'
                ? 'API tokens can only be created and managed for florist organizations.'
                : 'Use this token as a Bearer token when calling the API for this florist.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Typography variant="label" className="block mb-1">
                Token Type
              </Typography>
              <Typography variant="supporting">
                {tokenState?.kind === 'storefront_system'
                  ? 'Storefront system'
                  : tokenState?.kind === 'global_organization'
                    ? 'Global organization'
                    : 'Organization managed'}
              </Typography>
            </div>
            <div>
              <Typography variant="label" className="block mb-1">
                Service Account
              </Typography>
              <Typography variant="supporting">
                {tokenState?.service_account_email ?? 'Not created yet'}
              </Typography>
            </div>
            <div>
              <Typography variant="label" className="block mb-1">
                Created
              </Typography>
              <Typography variant="supporting">
                {formatTimestamp(tokenState?.created_at)}
              </Typography>
            </div>
            <div>
              <Typography variant="label" className="block mb-1">
                Last Used
              </Typography>
              <Typography variant="supporting">
                {formatTimestamp(tokenState?.last_used_at)}
              </Typography>
            </div>
            <div>
              <Typography variant="label" className="block mb-1">
                Updated
              </Typography>
              <Typography variant="supporting">
                {formatTimestamp(tokenState?.updated_at)}
              </Typography>
            </div>
            <div>
              <Typography variant="label" className="block mb-1">
                Expires
              </Typography>
              <Typography variant="supporting">
                {formatTimestamp(tokenState?.expires_at)}
              </Typography>
            </div>
          </dl>
        </CardContent>
        {canManage ? (
          <CardFooter className="flex flex-col gap-4 border-t sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              {!tokenState?.exists ? (
                <Button type="button" onClick={handleCreate} disabled={isBusy}>
                  <KeyRound />
                  Generate API Token
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleRegenerate}
                    disabled={isBusy}
                  >
                    <RefreshCcw />
                    Regenerate
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleRevoke}
                    disabled={isBusy}
                  >
                    <Trash2 />
                    Revoke
                  </Button>
                </>
              )}
            </div>

            {tokenState?.exists ? (
              <form
                onSubmit={handleValidate}
                className="flex w-full max-w-md flex-col gap-2 sm:w-auto"
              >
                <Typography variant="label">Validate a pasted token</Typography>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Input
                    value={validationToken}
                    onChange={(event) => setValidationToken(event.target.value)}
                    placeholder="Paste an API token"
                    aria-label="API token to validate"
                  />
                  <Button type="submit" variant="outline" disabled={isBusy} className="h-9">
                    Validate
                  </Button>
                </div>
              </form>
            ) : null}
          </CardFooter>
        ) : null}
      </Card>
    </div>
  )
}
